/**
 * The campaign's breakdown dimension: one control shared by the overview,
 * Território and Sentimento cards (owner ruling `owner-decisions-fe-data-
 * plane-frame-figures` ruling 5, the cross-cutting note on
 * `owner-decisions-fe-data-plane-frame-figures`) rather than three. A
 * dimension is the campaign's own active tag category (`useTagVocabulary`);
 * `value: null` means "let the route pick", the same default `read_breakdown`
 * applies when `dimension_id` is omitted, so this component never selects a
 * category by name.
 *
 * `useTagVocabulary` reads the vocabulary editor's own most-used-first order
 * (`read_vocabulary`), not the `tag_categories.sequence` order
 * `read_breakdown`'s default actually walks — no route exposes that order to
 * a client. The selector's own default follows this list's first entry,
 * which agrees with the route's default only where the two orders happen to
 * coincide (true for a freshly seeded campaign, whose first category is also
 * its most-used one); a campaign that has since reordered its categories can
 * see the selector default to a different one than the route would have
 * chosen unprompted. Owed to whichever task exposes `list_dimensions` at a
 * route.
 *
 * `BreakdownGroups` is the generic rendering of one breakdown's groups,
 * shared by Território and Sentimento (owner ruling: their cards read the
 * chosen dimension through this selector rather than through a fixed card).
 * The overview draws its own richer `InfluenceMap` instead, with the account
 * count, the chevron and the series toggle this generic list has no room for.
 */

import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useEffect } from 'react';

import { getLocale } from '../../../i18n';
import { count } from '../../../chrome/measure/format';
import { Absent } from '../../../chrome/measure';
import { RankRow } from '../../../chrome/chart';
import { EmptyState, QueryStates } from '../../../chrome/state';
import type { ApiFailureError } from '../../../lib/http/errors';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { useTagVocabulary } from '../../tenant/tags/api/queries';
import { TagGlyph, tagIconName } from '../../tenant/tags/icons';
import type { Breakdown } from '../api/schemas';
import { ds, dsPlural } from './messages';

export function BreakdownSelect(props: {
  campaignId: string;
  value: string | null;
  onChange: (dimensionId: string) => void;
}): ReactNode {
  const { onChange } = props;
  const vocabulary = useTagVocabulary(props.campaignId);
  const categories = (vocabulary.data ?? []).filter((category) => category.active);
  const defaultId = categories[0]?.id;

  // Lifts the route's own default (its first active category) into the
  // caller's state once the vocabulary is known, so a caller that reads a
  // group's figures already has an explicit dimension to key its own cache
  // and its chevron destinations on, rather than reading `null` forever.
  useEffect(() => {
    if (props.value === null && defaultId !== undefined) onChange(defaultId);
  }, [props.value, defaultId, onChange]);

  const selected = props.value ?? defaultId ?? '';

  return (
    <Select value={selected} onValueChange={props.onChange} disabled={categories.length === 0}>
      <SelectTrigger aria-label={ds('dimension.label')} className="max-w-48">
        <SelectValue placeholder={ds('dimension.placeholder')} />
      </SelectTrigger>
      <SelectContent>
        {categories.map((category) => (
          <SelectItem key={category.id} value={category.id}>
            <TagGlyph name={tagIconName(null, category)} />
            {category.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function BreakdownGroups(props: {
  query: UseQueryResult<Breakdown, ApiFailureError>;
}): ReactNode {
  return (
    <QueryStates
      query={props.query}
      isEmpty={(data) => data.groups.length === 0}
      empty={<EmptyState title={ds('groups.empty')} />}
    >
      {(breakdown) => (
        <ul className="flex flex-col gap-3">
          {breakdown.groups.map((group) => (
            <li key={group.value_id} className="flex flex-col gap-1">
              <RankRow
                label={group.name}
                value={
                  group.total === null ? (
                    <Absent reason="not_measured" />
                  ) : (
                    count(group.total, getLocale())
                  )
                }
              />
              <p data-numeric className="text-caption text-muted-foreground">
                {dsPlural('groups.accounts.other', group.account_count, {
                  n: count(group.account_count, getLocale()),
                })}
              </p>
            </li>
          ))}
        </ul>
      )}
    </QueryStates>
  );
}
